import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import CustomInput from "../components/ui/CustomInput";
import PrimaryButton from "../components/ui/PrimaryButton";
import { BodySmallRegular } from "../components/ui/Typography";
import { ReactComponent as UserIcon } from "../assets/ic_user.svg";
import { useCashOut } from "../hooks/useCashExchange";
import { useVerifyCustomer } from "../hooks/useVerification";
import toast from "react-hot-toast";
import { CashOutRequest } from "../api/services/cashExchange/type";
import { Customer } from "../api/services/cashExchange/verificationService";

interface CashOutFormData {
  playerId: string;
  amount: number;
}

export const CashOut: React.FC = () => {
  const [playerVerified, setPlayerVerified] = useState(false);
  const [verifiedPlayer, setVerifiedPlayer] = useState<Customer | null>(null);

  const cashOutMutation = useCashOut();
  const verifyCustomerMutation = useVerifyCustomer();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    reset,
  } = useForm<CashOutFormData>({
    defaultValues: {
      playerId: "",
      amount: 0,
    },
  });

  const watchPlayerId = watch("playerId");

  React.useEffect(() => {
    if (verifiedPlayer && verifiedPlayer.playerId !== watchPlayerId) {
      setPlayerVerified(false);
      setVerifiedPlayer(null);
    }
  }, [watchPlayerId, verifiedPlayer]);

  const onSubmit: SubmitHandler<CashOutFormData> = async (data) => {
    if (!playerVerified) {
      toast.error("Please verify the player ID first");
      return;
    }

    const cashOutData: CashOutRequest = {
      amount: data.amount,
      playerId: data.playerId,
    };

    try {
      await cashOutMutation.mutateAsync(cashOutData);
      reset();
      setPlayerVerified(false);
      setVerifiedPlayer(null);
    } catch (error) {
      console.error("Cash out submission error:", error);
    }
  };

  const handlePlayerVerify = async () => {
    const playerId = watch("playerId");
    if (playerId.trim()) {
      try {
        const response = await verifyCustomerMutation.mutateAsync(playerId);
        setPlayerVerified(true);
        setVerifiedPlayer(response.data);
      } catch {
        setPlayerVerified(false);
        setVerifiedPlayer(null);
      }
    } else {
      toast.error("Please enter a player ID first");
    }
  };

  const isLoading = isSubmitting || cashOutMutation.isPending;
  const isVerifyingPlayer = verifyCustomerMutation.isPending;

  return (
    <div className="p-6 bg-slate-900 min-h-screen">
      <div className="max-w-4xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Player ID */}
          <div>
            <BodySmallRegular className="text-gray-300 mb-2">
              Customer ID*
            </BodySmallRegular>
            <div className="flex gap-3">
              <div className="flex-1">
                <CustomInput
                  icon={<UserIcon className="w-5 h-5 text-slate-400" />}
                  placeholder="Enter customer ID"
                  type="text"
                  name="playerId"
                  register={register}
                  validation={{
                    required: "Player ID is required",
                  }}
                  error={errors.playerId}
                  disabled={isLoading}
                />
              </div>
              <button
                type="button"
                onClick={handlePlayerVerify}
                disabled={isVerifyingPlayer || isLoading}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  playerVerified
                    ? "bg-green-600 text-white"
                    : "bg-slate-600 hover:bg-slate--500 text-white disabled:bg-slate-700 disabled:text-slate-400"
                }`}
              >
                {isVerifyingPlayer
                  ? "Verifying..."
                  : playerVerified
                    ? "Verified"
                    : "Verify"}
              </button>
            </div>
          </div>
          {/* Player Information Display */}
          {playerVerified && verifiedPlayer && (
            <div className="bg-slate-800 border border-green-600 rounded-lg p-4">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {verifiedPlayer.photo ? (
                    <>
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/${verifiedPlayer.photo}`}
                        alt={verifiedPlayer.name}
                        className="w-16 h-16 rounded-full object-cover border-2 border-green-600"
                        onError={(e) => {
                          // Hide image and show fallback
                          const target = e.currentTarget as HTMLImageElement;
                          const fallback =
                            target.nextElementSibling as HTMLElement | null;
                          target.style.display = "none";
                          if (fallback) {
                            fallback.style.display = "flex";
                          }
                        }}
                      />
                      <div
                        className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600"
                        style={{ display: "none" }}
                      >
                        <UserIcon className="w-8 h-8 text-slate-300" />
                      </div>
                    </>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-slate-600 flex items-center justify-center border-2 border-green-600">
                      <UserIcon className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-medium text-lg">
                    {verifiedPlayer.name}
                  </h3>
                  <p className="text-slate-300 text-sm">
                    Email: {verifiedPlayer.email}
                  </p>
                  {verifiedPlayer.phone && (
                    <p className="text-slate-300 text-sm">
                      Phone: {verifiedPlayer.phone}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {/* Amount */}
          <div>
            <BodySmallRegular className="text-gray-300 mb-2">
              Cash Amount*
            </BodySmallRegular>
            <CustomInput
              placeholder="Enter amount"
              type="number"
              name="amount"
              register={register}
              validation={{
                required: "Cash amount is required",
                min: {
                  value: 0.01,
                  message: "Amount must be greater than 0",
                },
              }}
              error={errors.amount}
              disabled={isLoading}
            />
          </div>
          {/* Submit Button */}
          <div className="flex justify-end pt-6">
            <PrimaryButton
              type="submit"
              disabled={isLoading || !playerVerified}
              className="px-8 py-3"
            >
              {isLoading ? "Processing..." : "Cash Out"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </div>
  );
};
